<div class="page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Operations</p>
      <h1>Recurring Appointment Schedules</h1>
      <p class="subtitle">Create and manage recurring pickup plans without linking them to one-off appointments.</p>
    </div>
    <button class="refresh-btn" (click)="refreshSchedules()" [disabled]="loadingSchedules">
      <i class="fas fa-sync-alt"></i> Refresh
    </button>
  </div>

  <div class="grid">
    <div class="card">
      <h2>Create Schedule</h2>
      <p class="card-subtitle">Pick a customer, choose an address, then set the recurrence.</p>

      <div class="form-group">
        <label>Find Customer</label>
        <div class="search-row">
          <input type="text" placeholder="Search by name or phone" [(ngModel)]="searchTerm" (input)="onSearchInput()" (keyup.enter)="searchCustomers()" class="input" />
          <button type="button" class="secondary-btn" (click)="searchCustomers()">Search</button>
        </div>
        <div class="customer-pills" *ngIf="customers.length">
          <button class="pill" *ngFor="let customer of customers"
                  [class.active]="selectedCustomer?.customerId === customer.customerId"
                  (click)="selectCustomer(customer)">
            {{ customer.name }} • {{ customer.phoneNumber }}
          </button>
        </div>
      </div>

      <form [formGroup]="form" (ngSubmit)="submit()">
        <div class="form-group" *ngIf="selectedCustomer">
          <label>Pick Address *</label>
          <div class="address-list" *ngIf="addressOptions.length; else noAddress">
            <label *ngFor="let address of addressOptions" class="address-card" [class.error]="form.get('addressId')?.invalid && form.get('addressId')?.touched">
              <input type="radio" [value]="address.addressId" formControlName="addressId" (change)="selectAddress(address)" />
              <div>
                <div class="address-title">{{ address.address }}</div>
                <div class="address-meta">{{ address.locationName }} • {{ address.pincode }}</div>
              </div>
            </label>
          </div>
          <ng-template #noAddress>
            <p class="empty">No addresses found for this customer.</p>
          </ng-template>
          <p class="error" *ngIf="form.get('addressId')?.invalid && form.get('addressId')?.touched">Please select an address.</p>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label>Frequency *</label>
            <select class="input" formControlName="frequency">
              <option *ngFor="let option of frequencyOptions" [value]="option.value">{{ option.label }}</option>
            </select>
          </div>

          <div class="form-group" *ngIf="isWeekly()">
            <label>Days of Week *</label>
            <div class="chips">
              <button type="button" *ngFor="let day of dayOptions"
                      [class.active]="form.value.daysOfWeek?.includes(day.value)"
                      (click)="toggleDay(day.value)">
                {{ day.label }}
              </button>
            </div>
          </div>

          <div class="form-group" *ngIf="isMonthly()">
            <label>Days of Month *</label>
            <div class="month-grid">
              <button type="button"
                      *ngFor="let day of [].constructor(31); let idx = index"
                      [class.active]="form.value.monthlyDays?.includes(idx + 1)"
                      (click)="toggleMonthDay(idx + 1)">
                {{ idx + 1 }}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label>End Date *</label>
            <input type="date" class="input" formControlName="endDate" />
          </div>
        </div>

        <div class="actions">
          <div class="messages">
            <p class="error" *ngIf="errorMessage">{{ errorMessage }}</p>
            <p class="success" *ngIf="successMessage">{{ successMessage }}</p>
          </div>
          <button class="primary-btn" type="submit" [disabled]="saving || !isCustomerValidForSubmission()">
            <span *ngIf="saving"><i class="fas fa-spinner fa-spin"></i> Saving...</span>
            <span *ngIf="!saving"><i class="fas fa-plus-circle"></i> Create Schedule</span>
          </button>
        </div>
      </form>
    </div>

    <div class="card">
      <div class="card-header">
        <div>
          <h2>Schedules</h2>
          <p class="card-subtitle">Active and paused schedules. Mobile-friendly cards below.</p>
        </div>
        <span class="badge" *ngIf="schedules.length">{{ schedules.length }} total</span>
      </div>

      <div *ngIf="loadingSchedules" class="empty">
        <i class="fas fa-spinner fa-spin"></i> Loading schedules...
      </div>

      <div class="schedule-list" *ngIf="!loadingSchedules && schedules.length">
        <div class="schedule-card" *ngFor="let schedule of schedules">
          <div class="schedule-header">
            <div>
              <p class="eyebrow">Customer</p>
              <h3>{{ schedule.name }}</h3>
              <p class="meta">Id: {{ schedule.customerId }}</p>
              <p class="meta" *ngIf="schedule.phoneNumber">Phone: {{ schedule.phoneNumber }}</p>
              <p class="meta">Address: {{ schedule.address || schedule.addressId }}</p>
            </div>
            <span class="status"
                  [class.inactive]="!schedule.isActive">
              {{ schedule.isActive ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="schedule-body">
            <div class="chip-row">
              <span class="chip">Frequency: {{ schedule.recurringInfo.frequency === 1 ? 'Daily' : schedule.recurringInfo.frequency === 2 ? 'Weekly' : 'Monthly' }}</span>
              <span class="chip" *ngIf="schedule.recurringInfo.daysOfWeek?.length">Days: {{ schedule.recurringInfo.daysOfWeek?.join(', ') }}</span>
              <span class="chip" *ngIf="schedule.recurringInfo.monthlyDays?.length">Dates: {{ schedule.recurringInfo.monthlyDays?.join(', ') }}</span>
              <span class="chip" *ngIf="schedule.recurringInfo.endDate">Ends: {{ schedule.recurringInfo.endDate | date:'dd MMM yyyy' }}</span>
            </div>
          </div>

          <div class="actions">
            <button class="secondary-btn" *ngIf="!schedule.isActive" (click)="resume(schedule)">
              <i class="fas fa-play"></i>
              Activate
            </button>
            <button class="secondary-btn" *ngIf="schedule.isActive" (click)="pause(schedule)">
              <i class="fas fa-pause"></i>
              Pause
            </button>
            <button class="danger-btn" (click)="remove(schedule)"><i class="fas fa-trash"></i> Delete</button>
          </div>
        </div>
      </div>

      <div class="empty" *ngIf="!loadingSchedules && !schedules.length">
        <i class="fas fa-calendar-times"></i>
        <p>No recurring schedules yet.</p>
      </div>
    </div>
  </div>
</div>


